import { Button, Col, Form, Icons, Input, Row, Select, Spin, Typography, notification, Radio, useSelect, Upload, getValueFromEvent } from "@pankod/refine-antd";
import type { DefaultOptionType } from "antd/es/select";
import { useApiUrl, useGetIdentity, useOne, useUpdate } from "@pankod/refine-core";
import { useEffect, useMemo, useState } from "react";
import { IBaseProps, IDocument, ILookup, ILocations, IAssociateDetailsSnapshot, ISawAsset, ISawAssetMetadata, IState, IStateManager, IUser, nextState } from "interfaces";
import { arrayExtensions } from "lib-common";
import { DATAPROVIDER_CONFIGSET, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, DATAPROVIDER_UPDATE, DATAPROVIDER_UPLOAD, RESOURCE_PATH, fileUploadValidatorPdfWord, generateGroupFetchID } from "scripts/site";
import { ShowUserCard } from "components/profile/showUserCard";

const normalizeIdentifier = (value: unknown): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }
    const normalized = String(value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
    return normalized.length ? normalized : undefined;
};

const buildNormalizedSet = (values?: unknown[]): Set<string> => {
    return new Set(
        (values ?? [])
            .map(normalizeIdentifier)
            .filter((value): value is string => Boolean(value))
    );
};

const collectFieldSetIds = (stateDefinition?: IState, transition?: Partial<nextState>): Set<string> => {
    const transitionFieldSets = (transition as unknown as { fieldSetIds?: unknown[] })?.fieldSetIds ?? [];
    const combined: unknown[] = [
        ...(stateDefinition?.fieldSetIds ?? []),
        ...transitionFieldSets,
    ];
    return buildNormalizedSet(combined);
};

type AssociateSnapshot = IAssociateDetailsSnapshot;

const snapshotComparisonBaseKeys: (keyof AssociateSnapshot)[] = [
    "assignedTo",
    "nameEmail",
    "tcsEmployeeId",
    "projectName",
    "deliveryHead",
    "deliveryPartner",
    "associateLocation",
    "comments",
    "action",
    "stateId",
];

const sanitizeSnapshot = (input: Partial<AssociateSnapshot>): AssociateSnapshot => {
    const sanitized: Record<string, unknown> = {};
    (Object.keys(input) as (keyof AssociateSnapshot)[]).forEach((key) => {
        const value = input[key];
        if (value === undefined || value === null) {
            return;
        }
        sanitized[key as string] = Array.isArray(value) ? [...value] : value;
    });
    return sanitized as AssociateSnapshot;
};

const cloneAssociateSnapshot = (snapshot?: AssociateSnapshot | null): AssociateSnapshot | null => {
    if (!snapshot) {
        return null;
    }
    return sanitizeSnapshot(snapshot);
};

const cloneSnapshotArray = (snapshots?: AssociateSnapshot[]): AssociateSnapshot[] => {
    if (!Array.isArray(snapshots)) {
        return [];
    }
    return snapshots
        .map((entry) => cloneAssociateSnapshot(entry))
        .filter((entry): entry is AssociateSnapshot => Boolean(entry));
};

const areStringArraysEqual = (left?: string[], right?: string[]): boolean => {
    if (!left && !right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }
    if (left.length !== right.length) {
        return false;
    }
    return left.every((value, index) => value === right[index]);
};

const snapshotsEquivalent = (
    left?: AssociateSnapshot | null,
    right?: AssociateSnapshot | null,
    includeEvent = false,
): boolean => {
    if (!left && !right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }
    const keys = includeEvent
        ? [...snapshotComparisonBaseKeys, "event" as const]
        : snapshotComparisonBaseKeys;
    return keys.every((key) => {
        const leftValue = left[key];
        const rightValue = right[key];
        if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
            return areStringArraysEqual(leftValue as string[], rightValue as string[]);
        }
        return leftValue === rightValue;
    });
};

const hasMeaningfulAssociateValues = (snapshot?: AssociateSnapshot | null): boolean => {
    if (!snapshot) {
        return false;
    }
    const keysToTest: (keyof AssociateSnapshot)[] = [
        "assignedTo",
        "nameEmail",
        "tcsEmployeeId",
        "projectName",
        "deliveryHead",
        "deliveryPartner",
        "associateLocation",
    ];
    return keysToTest.some((key) => {
        const value = snapshot[key];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (typeof value === "string") {
            return value.trim().length > 0;
        }
        return value !== undefined;
    });
};

const prependSnapshotIfUnique = (
    collection: AssociateSnapshot[],
    snapshot: AssociateSnapshot,
): AssociateSnapshot[] => {
    const sanitizedSnapshot = sanitizeSnapshot(snapshot);
    if (collection.length === 0) {
        return [sanitizedSnapshot];
    }
    const [first] = collection;
    if (snapshotsEquivalent(first, sanitizedSnapshot)) {
        return collection;
    }
    return [sanitizedSnapshot, ...collection];
};

const appendHistoryEntry = (
    history: AssociateSnapshot[],
    snapshot: AssociateSnapshot,
    event: string,
): AssociateSnapshot[] => {
    const entry = sanitizeSnapshot({ ...snapshot, event });
    if (!entry.recordedAt) {
        entry.recordedAt = new Date().toISOString();
    }
    const updatedHistory = [...history];
    const lastEntry = updatedHistory[updatedHistory.length - 1];
    if (lastEntry && snapshotsEquivalent(lastEntry, entry, true)) {
        updatedHistory[updatedHistory.length - 1] = entry;
        return updatedHistory;
    }
    updatedHistory.push(entry);
    return updatedHistory;
};

const buildAssociateSnapshot = (input: Partial<AssociateSnapshot>): AssociateSnapshot | null => {
    const sanitized = sanitizeSnapshot(input);
    return Object.keys(sanitized).length > 0 ? sanitized : null;
};

type DocumentFormValue = IDocument & {
    url?: string;
    rawUrl?: string;
    filepath?: string;
};

const normalizeDocumentArray = (input?: unknown): DocumentFormValue[] => {
    if (!Array.isArray(input)) {
        return [];
    }

    return input
        .map((entry, index) => {
            if (!entry || typeof entry !== "object") {
                return undefined;
            }

            const doc = entry as DocumentFormValue;
            const responseUrl = doc.response?.url ?? doc.url ?? doc.rawUrl ?? doc.filepath;
            const normalized: DocumentFormValue = {
                ...doc,
                uid: doc.uid ?? `${index}`,
                status: doc.status ?? "done",
                name: doc.name ?? `Document-${index + 1}`,
                response: doc.response ?? (responseUrl ? { url: responseUrl } : doc.response),
            };

            if (responseUrl) {
                if (!normalized.response) {
                    normalized.response = { url: responseUrl };
                } else if (!normalized.response.url) {
                    normalized.response = { ...normalized.response, url: responseUrl };
                }
            }

            if (normalized.size === undefined) {
                normalized.size = doc.size ?? 0;
            }

            if (!normalized.type && doc.type) {
                normalized.type = doc.type;
            }

            return normalized;
        })
        .filter((doc): doc is DocumentFormValue => Boolean(doc));
};


type SawAssetFormValues = Omit<ISawAsset, "stateAction" | "deliveryPartner" | "associateLocation"> & {
    stateAction?: string;
    deliveryPartner?: string;
    associateLocation?: string;
    assignedTo?: string | string[];
    returnProofDocument?: DocumentFormValue[];
    nameEmail?: string;
    comments?: string;
};

type SawAssetConfigOverrides = Partial<ISawAssetMetadata["config"]> & {
    IdsCanAssignrole?: string[];
    idsCanAssignrole?: string[];
};

type SawAssetConfigSetResponse = {
    values?: SawAssetConfigOverrides;
};

const resolveAssignableRoleIds = (config?: {
    roleIdsCanAssign?: unknown;
    IdsCanAssignrole?: unknown;
    idsCanAssignrole?: unknown;
}): string[] => {
    if (!config) {
        return [];
    }

    const candidates: unknown[] = [
        (config as { roleIdsCanAssign?: unknown }).roleIdsCanAssign,
        config.IdsCanAssignrole,
        config.idsCanAssignrole,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length > 0) {
            return candidate.filter((value): value is string => typeof value === "string" && value.trim().length > 0);
        }
    }

    return [];
};

const normalizeAssignedTo = (value: string | string[] | undefined): string[] => {
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }
    return value ? [value] : [];
};

const buildSubmissionPayload = (
    values: SawAssetFormValues,
    context: {
        sawAssetRecord: ISawAsset;
        selectedAction?: nextState;
        showAllocationDetails: boolean;
        canEditProjectName: boolean;
        canEditDeliveryHead: boolean;
        canEditDeliveryPartner: boolean;
        canEditAssociateLocation: boolean;
        existingAllocationValues: {
            projectName?: string | null;
            deliveryHead?: string | null;
            deliveryPartner?: string | null;
            associateLocation?: string | null;
            tcsEmployeeId?: string | null;
        };
        existingReturnProofDocument: DocumentFormValue[];
        currentUser?: IUser | null;
    }
): Record<string, unknown> => {
    const {
        sawAssetRecord,
        selectedAction,
        showAllocationDetails,
        canEditProjectName,
        canEditDeliveryHead,
        canEditDeliveryPartner,
        canEditAssociateLocation,
        existingAllocationValues,
        existingReturnProofDocument,
        currentUser,
    } = context;

    const { returnProofDocument: formReturnProof, ...restValues } = values;
    const effectiveReturnProof = Array.isArray(formReturnProof) && formReturnProof.length > 0
        ? formReturnProof
        : existingReturnProofDocument;

    const submission: Record<string, unknown> = {
        ...restValues,
        stateManager: sawAssetRecord.stateManager,
        projectName: sawAssetRecord.projectName,
        deliveryHead: sawAssetRecord.deliveryHead,
        deliveryPartner: existingAllocationValues.deliveryPartner
            ? [existingAllocationValues.deliveryPartner]
            : [],
        associateLocation: existingAllocationValues.associateLocation,
        returnProofDocument: effectiveReturnProof,
    };

    if (selectedAction?.showAssignedTo) {
        const normalizedAssignedTo = normalizeAssignedTo(values.assignedTo);
        submission.assignedTo = normalizedAssignedTo[0] ?? values.assignedTo;
        submission.stateManager = {
            ...(sawAssetRecord.stateManager ?? {}),
            assignedTo: normalizedAssignedTo,
        };
    }

    if (showAllocationDetails && canEditProjectName) {
        submission.projectName = values.projectName;
    }

    if (showAllocationDetails && canEditDeliveryHead) {
        submission.deliveryHead = values.deliveryHead;
    }

    if (showAllocationDetails && canEditDeliveryPartner) {
        const partnerValue = values.deliveryPartner;
        submission.deliveryPartner = partnerValue ? [partnerValue] : [];
    }

    if (showAllocationDetails && canEditAssociateLocation) {
        submission.associateLocation = values.associateLocation;
    }

    const stateManager = submission.stateManager as IStateManager | undefined;
    const fallbackAssigned = Array.isArray(sawAssetRecord?.stateManager?.assignedTo)
        ? sawAssetRecord.stateManager?.assignedTo
        : sawAssetRecord?.assignedTo;
    const assignedArray = Array.isArray(stateManager?.assignedTo)
        ? (stateManager?.assignedTo ?? []).filter((entry): entry is string => Boolean(entry))
        : normalizeAssignedTo(
            Array.isArray(values.assignedTo)
                ? values.assignedTo
                : values.assignedTo ?? fallbackAssigned,
        );
    const deliveryPartnerEntry = submission.deliveryPartner;
    const deliveryPartnerArray = Array.isArray(deliveryPartnerEntry)
        ? (deliveryPartnerEntry as (string | undefined)[]).filter((entry): entry is string => Boolean(entry))
        : Array.isArray(sawAssetRecord.deliveryPartner)
            ? sawAssetRecord.deliveryPartner.filter((entry): entry is string => Boolean(entry))
            : [];
    const fallbackNameEmail = (sawAssetRecord as { nameEmail?: string })?.nameEmail;
    const nowIso = new Date().toISOString();
    const actionLabel = selectedAction?.action ?? values.stateAction ?? "update";
    const targetStateId = selectedAction?.stateId ?? sawAssetRecord?.stateManager?.state;
    const candidateSnapshot = buildAssociateSnapshot({
        recordedAt: nowIso,
        recordedBy: currentUser?.id,
        recordedByName: currentUser?.name,
        action: actionLabel,
        stateId: targetStateId,
        assignedTo: assignedArray,
        nameEmail: values.nameEmail ?? fallbackNameEmail,
        tcsEmployeeId: (submission.tcsEmployeeId as string | undefined) ?? sawAssetRecord?.tcsEmployeeId,
        projectName: submission.projectName as string | undefined,
        deliveryHead: submission.deliveryHead as string | undefined,
        deliveryPartner: deliveryPartnerArray,
        associateLocation: submission.associateLocation as string | undefined,
        comments: values.comments ?? undefined,
    });
    const meaningfulSnapshot = candidateSnapshot && hasMeaningfulAssociateValues(candidateSnapshot)
        ? candidateSnapshot
        : null;

    const existingCurrent = cloneAssociateSnapshot(sawAssetRecord?.current);
    const existingPrevious = cloneSnapshotArray(sawAssetRecord?.previous);
    const existingHistory = cloneSnapshotArray(sawAssetRecord?.history);
    const existingMeaningful = hasMeaningfulAssociateValues(existingCurrent);
    const hasExistingTracking = existingMeaningful || existingPrevious.length > 0 || existingHistory.length > 0;
    const shouldTrackDetails = hasExistingTracking || Boolean(meaningfulSnapshot);

    if (shouldTrackDetails) {
        let updatedCurrent: AssociateSnapshot | null = existingCurrent;
        let updatedPrevious = existingPrevious;
        let updatedHistory = existingHistory;

        const snapshotsMatch = snapshotsEquivalent(existingCurrent, meaningfulSnapshot);

        if (existingMeaningful && existingCurrent && (!meaningfulSnapshot || !snapshotsMatch)) {
            const previousEntry = buildAssociateSnapshot({
                ...existingCurrent,
                recordedAt: nowIso,
                recordedBy: currentUser?.id ?? existingCurrent?.recordedBy,
                recordedByName: currentUser?.name ?? existingCurrent?.recordedByName,
                action: actionLabel,
                stateId: targetStateId ?? existingCurrent?.stateId,
            });
            if (previousEntry) {
                updatedPrevious = prependSnapshotIfUnique(updatedPrevious, previousEntry);
                const previousEvent = meaningfulSnapshot ? "move-to-previous" : "clear-current";
                updatedHistory = appendHistoryEntry(updatedHistory, previousEntry, previousEvent);
            }
        }

        if (meaningfulSnapshot) {
            const currentEntry = buildAssociateSnapshot({
                ...meaningfulSnapshot,
                recordedAt: nowIso,
                recordedBy: currentUser?.id,
                recordedByName: currentUser?.name,
                action: actionLabel,
                stateId: targetStateId,
            });
            if (currentEntry && !snapshotsEquivalent(existingCurrent, currentEntry)) {
                updatedCurrent = currentEntry;
                const currentEvent = existingMeaningful ? "update-current" : "create-current";
                updatedHistory = appendHistoryEntry(updatedHistory, currentEntry, currentEvent);
            } else if (currentEntry && !updatedCurrent) {
                updatedCurrent = currentEntry;
            }
        } else {
            updatedCurrent = null;
            if (existingMeaningful) {
                const clearEntry = buildAssociateSnapshot({
                    recordedAt: nowIso,
                    recordedBy: currentUser?.id,
                    recordedByName: currentUser?.name,
                    action: actionLabel,
                    stateId: targetStateId,
                    assignedTo: [],
                    deliveryPartner: [],
                    comments: values.comments ?? undefined,
                });
                if (clearEntry) {
                    updatedHistory = appendHistoryEntry(updatedHistory, clearEntry, "clear-current");
                }
            }
        }

        submission.current = updatedCurrent ?? null;
        submission.previous = updatedPrevious;
        submission.history = updatedHistory;
    }

    return submission;
};

const { Title, Text } = Typography;

export declare type SawAssetActionProps = {
    sawAssetRecord: ISawAsset;
    sawAssetMetadata: ISawAssetMetadata;
    onFormSubmission: () => void;
    refetch: () => void;
    actions: nextState[] | undefined;
};

export const ManageSawAsset: React.FC<SawAssetActionProps> = ({
    sawAssetRecord, sawAssetMetadata, onFormSubmission, refetch, actions }) => {

    const [form] = Form.useForm<SawAssetFormValues>();
    const [selectedActionId, setSelectedActionId] = useState<string | undefined>(undefined);
    const { mutate } = useUpdate<IBaseProps>();
    const [savingForm, setSavingForm] = useState(false);
    const existingReturnProofDocument = useMemo(
        () => normalizeDocumentArray(sawAssetRecord?.returnProofDocument),
        [sawAssetRecord?.returnProofDocument]
    );
    const apiUrl = useApiUrl(DATAPROVIDER_UPLOAD);
    const uploadActionUrl = useMemo(() => {
        if (!sawAssetRecord?.id) {
            return undefined;
        }
        return `${apiUrl}/${RESOURCE_PATH.SAW_ASSET}/${sawAssetRecord.id}`;
    }, [apiUrl, sawAssetRecord?.id]);
    const { data: configSet } = useOne<SawAssetConfigSetResponse>({
        dataProviderName: DATAPROVIDER_CONFIGSET,
        resource: RESOURCE_PATH.SAW_ASSET,
        id: sawAssetRecord?.id ?? "",
        queryOptions: {
            enabled: !!sawAssetRecord?.id,
        },
    });
    const configOverrides = configSet?.data?.values;
    const effectiveConfig = useMemo(() => {
        const base = sawAssetMetadata?.config ? { ...sawAssetMetadata.config } : {} as ISawAssetMetadata["config"];
        return configOverrides ? ({ ...base, ...configOverrides } as ISawAssetMetadata["config"]) : base;
    }, [sawAssetMetadata?.config, configOverrides]);
    const { data: identity } = useGetIdentity();
    const currentUser = identity as IUser | undefined;
    const allowedAssignRoles = useMemo(() => resolveAssignableRoleIds(effectiveConfig), [effectiveConfig]);
    const canCurrentUserAssign = allowedAssignRoles.length === 0
        || (currentUser?.entityRoleIds ?? []).some(roleId => allowedAssignRoles.includes(roleId));
    const rowGutter: [number, number] = [16, 16];
    const twoColumnLayout = { xs: 24, md: 12 };

    // Reset form when record changes so we always start with the latest persisted values.
    useEffect(() => {
        setSelectedActionId(undefined);
    }, [sawAssetRecord?.id]);

    useEffect(() => {
        if (sawAssetRecord) {
            const currentAssignee = Array.isArray(sawAssetRecord.stateManager?.assignedTo)
                ? sawAssetRecord.stateManager?.assignedTo[0]
                : sawAssetRecord.assignedTo;
            form.resetFields();
            form.setFieldsValue({
                ...sawAssetRecord,
                assignedTo: currentAssignee || undefined,
                associateLocation: sawAssetRecord.associateLocation,
                deliveryPartner: sawAssetRecord.deliveryPartner && sawAssetRecord.deliveryPartner[0],
                projectName: sawAssetRecord.projectName,
                deliveryHead: sawAssetRecord.deliveryHead,
                tcsEmployeeId: sawAssetRecord.tcsEmployeeId,
                returnProofDocument: existingReturnProofDocument,
            });
        }
    }, [sawAssetRecord, form, existingReturnProofDocument]);

    useEffect(() => {
        form.setFieldsValue({ stateAction: selectedActionId });
    }, [form, selectedActionId]);

    useEffect(() => {
        if (!actions || actions.length !== 1) {
            return;
        }
        const [onlyAction] = actions;
        if (!onlyAction) {
            return;
        }
        if (selectedActionId !== onlyAction.action) {
            setSelectedActionId(onlyAction.action);
        }
        const currentValue = form.getFieldValue("stateAction");
        if (currentValue !== onlyAction.action) {
            form.setFieldsValue({ stateAction: onlyAction.action });
        }
    }, [actions, form, selectedActionId]);

    const selectedAction = useMemo(
        () => actions?.find((act) => act.action === selectedActionId),
        [actions, selectedActionId]
    );

    const nextStateFromAction = useMemo(() => {
        if (!selectedAction) {
            return undefined;
        }
        const states = sawAssetMetadata?.states ?? [];
        return states.find((st) => st.state === selectedAction.stateId);
    }, [selectedAction, sawAssetMetadata]);
    const fieldSetIds = useMemo(
        () => collectFieldSetIds(nextStateFromAction, selectedAction),
        [nextStateFromAction, selectedAction]
    );
    const assignedUsers = useMemo(() => {
        const stateManagerAssigned = Array.isArray(sawAssetRecord?.stateManager?.assignedTo)
            ? (sawAssetRecord?.stateManager?.assignedTo ?? []).filter(Boolean)
            : [];
        if (stateManagerAssigned.length) {
            return stateManagerAssigned;
        }
        return sawAssetRecord?.assignedTo ? [sawAssetRecord.assignedTo].filter(Boolean) : [];
    }, [sawAssetRecord?.assignedTo, sawAssetRecord?.stateManager?.assignedTo]);
    const existingAllocationValues = useMemo(() => {
        const normalizedDeliveryPartner = sawAssetRecord.deliveryPartner && sawAssetRecord.deliveryPartner[0];
        return {
            projectName: sawAssetRecord.projectName,
            deliveryHead: sawAssetRecord.deliveryHead,
            deliveryPartner: normalizedDeliveryPartner,
            associateLocation: sawAssetRecord.associateLocation,
            tcsEmployeeId: sawAssetRecord.tcsEmployeeId,
        };
    }, [sawAssetRecord]);

    useEffect(() => {
        if (!selectedAction?.predefinedComments) {
            return;
        }
        const currentComment = form.getFieldValue("comments");
        if (!currentComment) {
            form.setFieldValue("comments", selectedAction.predefinedComments);
        }
    }, [selectedAction, form]);

    const { selectProps: allocationUserSelect } = useSelect<ILookup>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.PEOPLE,
        optionLabel: "nameEmail",
        optionValue: "id",
        filters: [{ field: "stateManager.state", operator: "eq", value: "active" }],
        sort: [{ field: "lastAccessedAt", order: "desc" }],
        fetchSize: 100,
    });

    const { data: resourcingMetadata } = useOne({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.METADATA,
        id: RESOURCE_PATH.RESOURCINGREQUEST,
    });

    const deliveryHeadGroupId = resourcingMetadata?.data?.config?.deliveryHeadGroup;
    const deliveryPartnerOnsiteGroupId = resourcingMetadata?.data?.config?.deliveryPartnerOnsiteGroup;
    const deliveryPartnerOffshoreGroupId = resourcingMetadata?.data?.config?.deliveryPartnerOffshoreGroup;

    const { data: deliveryHeadGroup } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(deliveryHeadGroupId),
        queryOptions: {
            enabled: !!deliveryHeadGroupId,
        },
    });
    const deliveryHeadOptions = useMemo(() => {
        return arrayExtensions
            .sortByName(deliveryHeadGroup?.data || [])
            .map((item) => ({ value: item.id, label: item.name }));
    }, [deliveryHeadGroup]);

    const { data: deliveryPartnerOnsiteGroup, isLoading: deliveryPartnerOnsiteLoading } = useOne<ILookup[]>(
        {
            dataProviderName: DATAPROVIDER_READ,
            resource: RESOURCE_PATH.GROUPS,
            id: generateGroupFetchID(deliveryPartnerOnsiteGroupId),
            queryOptions: {
                enabled: !!deliveryPartnerOnsiteGroupId,
            },
        }
    );

    const { data: deliveryPartnerOffshoreGroup, isLoading: deliveryPartnerOffshoreLoading } = useOne<ILookup[]>(
        {
            dataProviderName: DATAPROVIDER_READ,
            resource: RESOURCE_PATH.GROUPS,
            id: generateGroupFetchID(deliveryPartnerOffshoreGroupId),
            queryOptions: {
                enabled: !!deliveryPartnerOffshoreGroupId,
            },
        }
    );

    const metadataDeliveryPartnerOptions = useMemo(() => {
        const onsite = arrayExtensions
            .sortByName(deliveryPartnerOnsiteGroup?.data || [])
            .map((item) => ({ value: item.id, label: item.name }));
        const offshore = arrayExtensions
            .sortByName(deliveryPartnerOffshoreGroup?.data || [])
            .map((item) => ({ value: item.id, label: item.name }));
        const combined = [...onsite, ...offshore];
        if (!combined.length) {
            return combined;
        }
        const seen = new Set<string>();
        return combined.filter((option) => {
            if (seen.has(option.value)) {
                return false;
            }
            seen.add(option.value);
            return true;
        });
    }, [deliveryPartnerOnsiteGroup, deliveryPartnerOffshoreGroup]);

    const locationFilters = useMemo(() => {
        if (sawAssetRecord?.countryId) {
            return [{ field: "countryId", operator: "eq" as const, value: sawAssetRecord.countryId }];
        }
        return undefined;
    }, [sawAssetRecord?.countryId]);

    const { selectProps: associateLocationSelectProps } = useSelect<ILocations>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LOCATION,
        optionLabel: "name",
        optionValue: "id",
        sort: [{ field: "name", order: "asc" }],
        fetchSize: 100,
        filters: locationFilters,
        queryOptions: {
            enabled: true,
        },
    });

    const allocationUserOptions = useMemo(
        () => (allocationUserSelect.options || []) as DefaultOptionType[],
        [allocationUserSelect.options],
    );
    const associateLocationOptions = useMemo(
        () => associateLocationSelectProps.options ?? [],
        [associateLocationSelectProps.options]
    );
    const deliveryPartnerOptions = useMemo(() => {
        if (metadataDeliveryPartnerOptions.length > 0) {
            return metadataDeliveryPartnerOptions;
        }
        return (allocationUserOptions || []).map((option) => ({
            value: option.value,
            label: option.label,
        }));
    }, [allocationUserOptions, metadataDeliveryPartnerOptions]);
    const deliveryPartnerLoading = deliveryPartnerOnsiteLoading || deliveryPartnerOffshoreLoading;
    const expectsAssignmentDetails = fieldSetIds.has("assignmentdetails") || Boolean(selectedAction?.showAssignedTo);
    const expectsAllocationDetails = fieldSetIds.has("allocationdetails");
    const expectsReturnProofFieldSet = fieldSetIds.has("returnproofdocument");
    const renderAssignmentFields = expectsAssignmentDetails;
    const renderAllocationDetails = expectsAllocationDetails;
    const allowAssignmentEditing = renderAssignmentFields && canCurrentUserAssign;
    const allowAllocationEditing = renderAllocationDetails && canCurrentUserAssign;
    const showAssignmentFields = renderAssignmentFields;
    const showAllocationDetails = renderAllocationDetails;
    const shouldShowReturnProofUpload = expectsReturnProofFieldSet;
    const requiresReturnProof = expectsReturnProofFieldSet && existingReturnProofDocument.length === 0;
    const canEditProjectName = allowAllocationEditing;
    const canEditDeliveryHead = allowAllocationEditing;
    const canEditDeliveryPartner = allowAllocationEditing;
    const canEditAssociateLocation = allowAllocationEditing;
    const summaryAssignedUsers = assignedUsers;
    const summaryProjectName = existingAllocationValues.projectName;
    const summaryDeliveryHead = existingAllocationValues.deliveryHead;
    const summaryDeliveryPartner = existingAllocationValues.deliveryPartner;
    const summaryAssociateLocation = existingAllocationValues.associateLocation;
    const summaryTcsEmployeeId = existingAllocationValues.tcsEmployeeId ?? sawAssetRecord?.tcsEmployeeId ?? undefined;
    const primaryDeliveryPartnerId = summaryDeliveryPartner;
    const associateLocationName = useMemo(() => {
        if (!summaryAssociateLocation) {
            return "---";
        }
        const located = associateLocationOptions.find((option) => option.value === summaryAssociateLocation);
        return located?.label ?? summaryAssociateLocation;
    }, [associateLocationOptions, summaryAssociateLocation]);
    const shouldShowAssignmentSummary = !showAssignmentFields;
    const showAssignmentSummary = shouldShowAssignmentSummary && summaryAssignedUsers.length > 0 && !shouldShowReturnProofUpload;
    const shouldShowAllocationSummary = !showAllocationDetails && !shouldShowReturnProofUpload;

    const allocationValidationMessage = "This action requires assignment details.";
    const returnProofValidationMessage = "Upload proof before returning the asset.";

    useEffect(() => {
        if (!selectedAction) {
            return;
        }

        if (!allowAllocationEditing && !allowAssignmentEditing) {
            return;
        }

        const assignedValue = assignedUsers[0] ?? undefined;

        form.setFieldValue("assignedTo", assignedValue);
        form.setFieldsValue({
            tcsEmployeeId: existingAllocationValues.tcsEmployeeId ?? undefined,
            projectName: existingAllocationValues.projectName ?? undefined,
            deliveryHead: existingAllocationValues.deliveryHead ?? undefined,
            associateLocation: existingAllocationValues.associateLocation ?? undefined,
            deliveryPartner: existingAllocationValues.deliveryPartner ?? undefined,
        });
    }, [selectedAction, form, allowAllocationEditing, allowAssignmentEditing, assignedUsers, existingAllocationValues]);

    const extractStringLabel = (label: unknown): string | undefined => {
        if (typeof label === "string") {
            return label;
        }

        if (Array.isArray(label)) {
            const text = label
                .map((entry) => (typeof entry === "string" ? entry : undefined))
                .filter((entry): entry is string => Boolean(entry))
                .join(" ")
                .trim();

            return text.length > 0 ? text : undefined;
        }

        return undefined;
    };

    const resolveNameEmail = (userId?: string): string | undefined => {
        if (!userId) {
            return undefined;
        }

        const matched = allocationUserOptions.find((option) => {
            if (option?.value === undefined || option?.value === null) {
                return false;
            }
            return String(option.value) === userId;
        });
        return extractStringLabel(matched?.label);
    };

    const saveForm = async () => {
        try {
            setSavingForm(true);
            const values = await form.validateFields();
            const fallbackNameEmail = (sawAssetRecord as { nameEmail?: string })?.nameEmail;
            const assignedFromForm = showAssignmentFields
                ? (Array.isArray(values.assignedTo) ? values.assignedTo[0] : values.assignedTo)
                : summaryAssignedUsers[0];
            const resolvedNameEmail = assignedFromForm
                ? resolveNameEmail(assignedFromForm) ?? fallbackNameEmail
                : (showAssignmentFields ? undefined : fallbackNameEmail);

            const normalizedValues: SawAssetFormValues = {
                ...values,
                nameEmail: resolvedNameEmail,
            };

            const submission = buildSubmissionPayload(normalizedValues, {
                sawAssetRecord,
                selectedAction,
                showAllocationDetails,
                canEditProjectName,
                canEditDeliveryHead,
                canEditDeliveryPartner,
                canEditAssociateLocation,
                existingAllocationValues,
                existingReturnProofDocument,
                currentUser,
            });

            mutate({
                dataProviderName: DATAPROVIDER_UPDATE,
                resource: RESOURCE_PATH.SAW_ASSET,
                id: sawAssetRecord?.id,
                values: submission,
                successNotification: {
                    key: "notificationKey",
                    message: "Successful",
                    description: "Successfully updated the SAW Asset",
                    type: "success",
                },
                errorNotification: {
                    key: "notificationKey",
                    message: "Error occured",
                    description: "Unable to update the SAW Asset",
                    type: "error",
                },
            },
                {
                    onSuccess: () => {
                        onFormSubmission();
                        refetch();
                    },
                    onError: () => {
                        onFormSubmission();
                    },
                    onSettled: () => {
                        setSavingForm(false);
                    },
                }
            );
        } catch (error) {
            if ((error as { errorFields?: unknown[] })?.errorFields) {
                notification.error({
                    message: "Incomplete Details",
                    description: "Please validate all the fields and save.",
                });
            }
            setSavingForm(false);
        }
    };

    return (
    <Spin spinning={savingForm}>
            <Form
                layout="vertical"
                initialValues={{
                    ...sawAssetRecord,
                    // stateAction intentionally applied via effect to survive resets
                    associateLocation: sawAssetRecord.associateLocation,
                    deliveryPartner: sawAssetRecord.deliveryPartner && sawAssetRecord.deliveryPartner[0],
                    returnProofDocument: existingReturnProofDocument,
                }}
                autoComplete="off"
                form={form}
            >
                {actions?.length === 1
                    ? <Form.Item name="stateAction" hidden={true}>
                        <Input />
                    </Form.Item>
                    : <Form.Item
                        label="SAW Asset Status"
                        name="stateAction"
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                            },
                        ]}
                    >
                        <Radio.Group
                            options={actions?.map((act) => ({
                                label: act.buttonText,   
                                value: act.action,        
                            }))}
                            optionType="button"
                            buttonStyle="solid"
                            onChange={(event) => setSelectedActionId(event.target.value)}
                        />
                    </Form.Item>
                }

                <Form.Item name="comments" hidden={true}>
                    <Input />
                </Form.Item>

                {shouldShowAllocationSummary && (
                    <>
                        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
                            {showAssignmentSummary && (
                                <Col className="gutter-row" {...twoColumnLayout}>
                                    <Title level={5}>Emp Name (Allocated to)</Title>
                                    {summaryAssignedUsers.length > 0
                                        ? summaryAssignedUsers.map((id, index) => <ShowUserCard key={id || index} id={id} />)
                                        : <Text>---</Text>}
                                </Col>
                            )}
                            <Col
                                className="gutter-row"
                                {...(showAssignmentSummary ? twoColumnLayout : { xs: 24 })}
                            >
                                <Title level={5}>TCS Employee Id</Title>
                                <Text copyable={!!summaryTcsEmployeeId}>{summaryTcsEmployeeId ?? "---"}</Text>
                            </Col>
                        </Row>
                        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
                            <Col className="gutter-row" {...twoColumnLayout}>
                                <Title level={5}>Project Name</Title>
                                <Text>{summaryProjectName ?? "---"}</Text>
                            </Col>
                            <Col className="gutter-row" {...twoColumnLayout}>
                                <Title level={5}>Associate Location</Title>
                                <Text>{associateLocationName}</Text>
                            </Col>
                        </Row>
                        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
                            <Col className="gutter-row" {...twoColumnLayout}>
                                <Title level={5}>Delivery Head (L1)</Title>
                                {summaryDeliveryHead
                                    ? <ShowUserCard id={summaryDeliveryHead} />
                                    : <Text>---</Text>}
                            </Col>
                            <Col className="gutter-row" {...twoColumnLayout}>
                                <Title level={5}>Delivery Partner (L2)</Title>
                                {primaryDeliveryPartnerId
                                    ? <ShowUserCard id={primaryDeliveryPartnerId} />
                                    : <Text>---</Text>}
                            </Col>
                        </Row>
                    </>
                )}

                {/* Assignment field - show when action requires assignedTo (showAssignedTo: true) */}
                {showAssignmentFields &&
                    <>
                        <Form.Item
                            label="Assigned To"
                            name="assignedTo"
                            tooltip="Email address of the person this asset is assigned to"
                            rules={allowAssignmentEditing ? [
                                {
                                    required: true,
                                    message: allocationValidationMessage,
                                },
                            ] : []}
                        >
                            <Select
                                {...allocationUserSelect}
                                options={allocationUserOptions}
                                placeholder="Search Name or Email"
                                optionFilterProp="label"
                                showSearch
                                allowClear
                                disabled={!allowAssignmentEditing}
                            />
                        </Form.Item>
                    </>
                }

                {showAllocationDetails && (
                    <>
                        <Form.Item
                            label="TCS Employee Id"
                            name="tcsEmployeeId"
                            rules={allowAllocationEditing ? [
                                {
                                    required: true,
                                    message: allocationValidationMessage,
                                },
                                {
                                    pattern: /^\d{6,8}$/,
                                    message: "Enter a 6-8 digit numeric ID",
                                },
                            ] : []}
                        >
                            <Input placeholder="Enter TCS Employee Id" maxLength={8} disabled={!allowAllocationEditing} />
                        </Form.Item>

                        <Form.Item
                            label="Associate Location"
                            name="associateLocation"
                            rules={canEditAssociateLocation ? [
                                {
                                    required: true,
                                    message: allocationValidationMessage,
                                },
                            ] : []}
                        >
                            <Select
                                {...associateLocationSelectProps}
                                options={associateLocationOptions}
                                allowClear
                                showSearch
                                disabled={!canEditAssociateLocation}
                                placeholder="Select Associate Location"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Project Name"
                            name="projectName"
                            rules={canEditProjectName ? [
                                {
                                    required: true,
                                    whitespace: true,
                                    message: allocationValidationMessage,
                                },
                            ] : []}
                        >
                            <Input
                                placeholder="Enter Project Name"
                                disabled={!canEditProjectName}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Delivery Head (L1)"
                            name="deliveryHead"
                            rules={canEditDeliveryHead ? [
                                {
                                    required: true,
                                    message: allocationValidationMessage,
                                },
                            ] : []}
                        >
                            <Select
                                placeholder="Select Delivery Head"
                                options={deliveryHeadOptions}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                loading={!deliveryHeadGroup && !!deliveryHeadGroupId}
                                disabled={!canEditDeliveryHead}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Delivery Partner (L2)"
                            name="deliveryPartner"
                            rules={canEditDeliveryPartner ? [
                                {
                                    required: true,
                                    validator: (_, value) =>
                                        value
                                            ? Promise.resolve()
                                            : Promise.reject(new Error(allocationValidationMessage)),
                                },
                            ] : []}
                        >
                            <Select
                                options={deliveryPartnerOptions}
                                optionFilterProp="label"
                                showSearch
                                allowClear
                                disabled={!canEditDeliveryPartner}
                                placeholder="Select Delivery Partner"
                                loading={deliveryPartnerLoading}
                            />
                        </Form.Item>
                    </>
                )}
                {shouldShowReturnProofUpload && (
                    <Form.Item
                        label="Return proof document"
                        name="returnProofDocument"
                        valuePropName="fileList"
                        getValueFromEvent={getValueFromEvent}
                        tooltip="Size limit 5 MB"
                        rules={[
                            {
                                required: requiresReturnProof,
                                message: returnProofValidationMessage,
                            },
                            {
                                validator: (_, value) => {
                                    if (!requiresReturnProof) {
                                        return Promise.resolve();
                                    }
                                    const documents = normalizeDocumentArray(value);
                                    if (documents.length > 0) {
                                        return Promise.resolve();
                                    }
                                    if (existingReturnProofDocument.length > 0) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(returnProofValidationMessage));
                                },
                            },
                        ]}
                    >
                        <Upload
                            name="file"
                            accept=".pdf,.doc,.docx,.eml,.msg,.png,.jpg,.jpeg,.gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,message/rfc822,application/vnd.ms-outlook,image/png,image/jpeg,image/gif"
                            action={uploadActionUrl}
                            maxCount={1}
                            beforeUpload={fileUploadValidatorPdfWord(true)}
                        >
                            <Button icon={<Icons.UploadOutlined />}>Upload</Button>
                        </Upload>
                    </Form.Item>
                )}
                <Form.Item>
                    <Button
                        type="primary"
                        onClick={saveForm}
                        style={{ backgroundColor: "green" }}
                        disabled={savingForm}
                        loading={savingForm}
                        icon={<Icons.SaveOutlined />}
                    >
                        Save
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
}